import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaClipboardList, FaListUl, FaMoon, FaTerminal } from "react-icons/fa";
import { FaCircleNodes } from "react-icons/fa6";
import { FiPackage } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import NoctisStatusDot from "../../components/noctis/NoctisStatusDot";
import {
  LdsOverviewCard,
  LdsOverviewGrid,
  LdsOverviewHero,
  LdsOverviewPage,
} from "../../components/lds/LdsOverview";
import config from "../../config";

type NoctisExternalCardProps = {
  href: string;
  eyebrow: string;
  title: string;
  description: string;
  actionLabel: string;
  animationIndex: number;
};

const NoctisExternalCard = ({
  href,
  eyebrow,
  title,
  description,
  actionLabel,
  animationIndex,
}: NoctisExternalCardProps) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="lds-overview-card-enter group relative flex min-h-[260px] flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white p-6 text-left shadow-sm transition duration-300 hover:-translate-y-1 hover:border-indigo-200 hover:shadow-[0_24px_60px_rgba(79,70,229,0.16)] dark:border-white/10 dark:bg-slate-900/80 dark:hover:border-indigo-400/30"
    style={{ animationDelay: `${animationIndex * 80}ms` }}
  >
    <div className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-violet-500 via-indigo-500 to-sky-400 transition-transform duration-500 group-hover:scale-x-100" />
    <FaMoon
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-8 -right-7 text-[9rem] text-indigo-200/55 dark:text-indigo-400/10"
    />
    <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-100 to-sky-50 text-2xl text-violet-700 shadow-sm dark:from-violet-800/40 dark:to-sky-900/30 dark:text-violet-200">
      <FaMoon aria-hidden="true" />
    </div>
    <div className="relative z-10 mt-5 flex-1">
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-indigo-700 dark:text-indigo-300">
        {eyebrow}
      </p>
      <h2 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">{title}</h2>
      <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">{description}</p>
    </div>
    <span className="relative z-10 mt-6 text-sm font-bold text-indigo-700 dark:text-indigo-200">
      {actionLabel}
    </span>
  </a>
);

const NoctisOverview = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const noctisUiUrl = useMemo(() => {
    const explicit = String(config?.noctisUiUrl || "").trim();
    if (explicit) return explicit;

    const raw = String(config?.integrationApiBaseUrl || "").trim();
    if (!raw) return "/noctis/console";
    const stripped = raw.replace(/\/+$/, "");
    const derived = stripped.replace(/\/api(?:\/v\d+)?$/i, "") || "";
    if (!derived || derived === "/") return "/noctis/console";
    return derived;
  }, []);

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaMoon}
        eyebrow={t("navigation.tools", { defaultValue: "Tools" })}
        title={t("noctis.menu", { defaultValue: "Noctis" })}
        description={t("noctis.overview.description", {
          defaultValue: "Beheer de Noctis-koppeling, queue, logs en referentiestatus.",
        })}
        actions={<NoctisStatusDot />}
      />

      <LdsOverviewGrid>
        <LdsOverviewCard
          icon={FiPackage}
          eyebrow={t("noctis.overview.category_config", { defaultValue: "Configuratie" })}
          title={t("noctis.object_types.title", { defaultValue: "Objecttypen" })}
          description={t("noctis.object_types.description", {
            defaultValue: "Bekijk objecttypen die beschikbaar zijn via de Noctis-koppeling.",
          })}
          actionLabel={t("common.open", { defaultValue: "Open" })}
          onClick={() => navigate("/noctis/object-types")}
          animationIndex={0}
        />
        <LdsOverviewCard
          icon={FaListUl}
          eyebrow={t("noctis.overview.category_queue", { defaultValue: "Queue" })}
          title={t("noctis.outbox.title", { defaultValue: "Noctis queue" })}
          description={t("noctis.outbox.description", { defaultValue: "Bekijk de outbox en retry status." })}
          actionLabel={t("common.open", { defaultValue: "Open" })}
          onClick={() => navigate("/noctis/queue")}
          animationIndex={1}
        />
        <LdsOverviewCard
          icon={FaClipboardList}
          eyebrow={t("noctis.overview.category_audit", { defaultValue: "Logging" })}
          title={t("noctis.events.title", { defaultValue: "Noctis logs" })}
          description={t("noctis.events.description", {
            defaultValue: "Bekijk events en logging van de koppeling.",
          })}
          actionLabel={t("common.open", { defaultValue: "Open" })}
          onClick={() => navigate("/noctis/events")}
          animationIndex={2}
        />
        <LdsOverviewCard
          icon={FaTerminal}
          eyebrow={t("noctis.overview.category_tools", { defaultValue: "Tools" })}
          title={t("noctis.console.title", { defaultValue: "Noctis console" })}
          description={t("noctis.console.description", {
            defaultValue: "Voer Noctis-actie(s) uit en bekijk responses.",
          })}
          actionLabel={t("common.open", { defaultValue: "Open" })}
          onClick={() => navigate("/noctis/console")}
          animationIndex={3}
        />
        <LdsOverviewCard
          icon={FaCircleNodes}
          eyebrow={t("noctis.overview.category_status", { defaultValue: "Status" })}
          title={t("noctis.status.title", { defaultValue: "Referentie status" })}
          description={t("noctis.status.description", {
            defaultValue: "Bekijk de synchronisatie-status van referentiedata.",
          })}
          actionLabel={t("common.open", { defaultValue: "Open" })}
          onClick={() => navigate("/noctis/reference-status")}
          animationIndex={4}
        />
        <NoctisExternalCard
          href={noctisUiUrl}
          eyebrow={t("noctis.overview.category_external", { defaultValue: "Extern" })}
          title={t("noctis.menu", { defaultValue: "Noctis" })}
          description={t("noctis.open_new_tab.description", {
            defaultValue: "Open Noctis in een nieuw tabblad.",
          })}
          actionLabel={t("common.open", { defaultValue: "Open" })}
          animationIndex={5}
        />
      </LdsOverviewGrid>
    </LdsOverviewPage>
  );
};

export default NoctisOverview;
